import React, { useState, useRef, Component } from 'react';
import { SEOHead } from '../../components/SEOHead';
import { ScrollReveal } from '../../components/ScrollReveal';
import { CheckCircleIcon, CopyIcon, AlertCircleIcon, Wand2Icon, UploadIcon, LinkIcon, XIcon, SendIcon, LoaderIcon, LockIcon } from 'lucide-react';
// Replace this with your actual GHL webhook URL
const GHL_WEBHOOK_URL = 'https://services.leadconnectorhq.com/hooks/JLycxFbUVQFY3z4zPpCS/webhook-trigger/1388343a-44e6-4563-8cd2-2acd1a8562be';
// Define the social networks
const SOCIAL_NETWORKS = [{
  id: 'linkedin',
  name: 'LinkedIn',
  icon: 'https://img.icons8.com/color/48/000000/linkedin.png',
  defaultUrl: 'https://linkedin.com/'
}, {
  id: 'yelp',
  name: 'Yelp',
  icon: 'https://img.icons8.com/color/48/000000/yelp.png',
  defaultUrl: ''
}, {
  id: 'google',
  name: 'Google Business',
  icon: 'https://img.icons8.com/color/48/000000/google-logo.png',
  defaultUrl: ''
}, {
  id: 'instagram',
  name: 'Instagram',
  icon: 'https://img.icons8.com/color/48/000000/instagram-new.png',
  defaultUrl: 'https://instagram.com/'
}, {
  id: 'facebook',
  name: 'Facebook',
  icon: 'https://img.icons8.com/color/48/000000/facebook-new.png',
  defaultUrl: ''
}, {
  id: 'twitter',
  name: 'Twitter / X',
  icon: 'https://img.icons8.com/color/48/000000/twitter--v1.png',
  defaultUrl: ''
}, {
  id: 'youtube',
  name: 'YouTube',
  icon: 'https://img.icons8.com/color/48/000000/youtube-play.png',
  defaultUrl: ''
}, {
  id: 'tiktok',
  name: 'TikTok',
  icon: 'https://img.icons8.com/color/48/000000/tiktok--v1.png',
  defaultUrl: ''
}, {
  id: 'pinterest',
  name: 'Pinterest',
  icon: 'https://img.icons8.com/color/48/000000/pinterest--v1.png',
  defaultUrl: ''
}];
export function GmailSignatureGeneratorPage() {
  // State for all inputs
  const [themeColor, setThemeColor] = useState('#10B981'); // viridia green
  const [logoSize, setLogoSize] = useState(130);
  const [logoUrl, setLogoUrl] = useState('https://images.unsplash.com/photo-1614680376573-3e4e1ef4142a?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80');
  const [personalInfo, setPersonalInfo] = useState({
    name: 'Jane Doe',
    role: 'Marketing Director',
    phone: '(555) 123-4567',
    email: 'hello@yourcompany.com',
    location: 'Phoenix, AZ',
    website: 'www.yourcompany.com'
  });
  // Initialize social links state
  const initialSocials = SOCIAL_NETWORKS.reduce((acc, net) => {
    acc[net.id] = net.defaultUrl;
    return acc;
  }, {} as Record<string, string>);
  const [socialLinks, setSocialLinks] = useState(initialSocials);
  // Toast state
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const signatureRef = useRef<HTMLDivElement>(null);
  // Opt-in gate state
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [showOptIn, setShowOptIn] = useState(false);
  const [optInData, setOptInData] = useState({
    name: '',
    email: '',
    businessName: ''
  });
  const [consentChecked, setConsentChecked] = useState(false);
  const [optInSubmitting, setOptInSubmitting] = useState(false);
  const [optInError, setOptInError] = useState('');
  // Handlers
  const handleInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPersonalInfo({
      ...personalInfo,
      [e.target.name]: e.target.value
    });
  };
  const handleSocialChange = (id: string, value: string) => {
    setSocialLinks({
      ...socialLinks,
      [id]: value
    });
  };
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = event => {
        if (event.target?.result) {
          setLogoUrl(event.target.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({
      message,
      type
    });
    setTimeout(() => setToast(null), 3000);
  };
  const copySignature = () => {
    if (!signatureRef.current) return;
    try {
      const range = document.createRange();
      range.selectNode(signatureRef.current);
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
        const successful = document.execCommand('copy');
        if (successful) {
          showToast('Success! Signature copied to clipboard.', 'success');
        } else {
          showToast('Failed to copy. Please select it manually.', 'error');
        }
        selection.removeAllRanges();
      }
    } catch (err) {
      showToast('Your browser does not support auto-copying. Please select it manually.', 'error');
    }
  };
  const handleCopyClick = () => {
    if (isUnlocked) {
      copySignature();
    } else {
      setShowOptIn(true);
    }
  };
  const handleOptInSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setOptInSubmitting(true);
    setOptInError('');
    try {
      // Use no-cors mode to avoid CORS blocking on GHL webhook
      // With no-cors, we can't read the response, but the POST still reaches GHL
      await fetch(GHL_WEBHOOK_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'text/plain'
        },
        body: JSON.stringify({
          firstName: optInData.name.split(' ')[0],
          lastName: optInData.name.split(' ').slice(1).join(' '),
          email: optInData.email,
          companyName: optInData.businessName,
          source: 'Gmail Signature Generator'
        })
      });
      setIsUnlocked(true);
      setShowOptIn(false);
      // Auto-copy after unlocking
      setTimeout(() => copySignature(), 300);
    } catch {
      // Even if the fetch appears to fail, the data likely reached GHL
      // Unlock anyway so the user isn't stuck
      setIsUnlocked(true);
      setShowOptIn(false);
      setTimeout(() => copySignature(), 300);
    } finally {
      setOptInSubmitting(false);
    }
  };
  // Derived values for the signature
  const hexNoHash = themeColor.replace('#', '');
  const formattedPhone = personalInfo.phone.replace(/[^0-9+]/g, '');
  const formattedWebsite = personalInfo.website.startsWith('http') ? personalInfo.website : `https://${personalInfo.website}`;
  const encodedLocation = encodeURIComponent(personalInfo.location);
  return <div className="bg-viridia-black min-h-screen pt-24 pb-20" data-id="element-2010">
      <SEOHead title="Free Gmail Signature Generator | Viridia Analytics" description="Create a professional, mobile-friendly email signature for Gmail. Customize colors, add your logo, and include social links. 100% free tool." canonical="/tools/gmail-signature-generator" data-id="element-2011" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2012">
        <ScrollReveal data-id="element-2013">
          <header className="mb-12 text-center max-w-3xl mx-auto" data-id="element-2014">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 text-viridia text-sm font-semibold tracking-wide uppercase mb-4 border border-viridia/20" data-id="element-2015">
              FREE TOOL
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4" data-id="element-2016">
              Gmail Signature Generator
            </h1>
            <p className="text-gray-400 text-lg" data-id="element-2017">
              Customize your details, then copy and paste directly into your
              Gmail settings. No coding required.
            </p>
          </header>
        </ScrollReveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-2018">
          {/* LEFT COLUMN: CONTROLS */}
          <div className="lg:col-span-5 xl:col-span-4 space-y-6" data-id="element-2019">
            {/* 1. Branding & Logo */}
            <div className="bg-viridia-surface rounded-xl p-6 border border-gray-800" data-id="element-2020">
              <h2 className="text-xl font-bold text-white mb-4 pb-2 border-b border-gray-800" data-id="element-2021">
                1. Branding & Logo
              </h2>
              <div className="space-y-5" data-id="element-2022">
                <div data-id="element-2023">
                  <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-2024">
                    Brand Color
                  </label>
                  <div className="flex items-center gap-3" data-id="element-2025">
                    <input type="color" value={themeColor} onChange={e => setThemeColor(e.target.value)} className="h-10 w-20 rounded cursor-pointer border-0 bg-transparent p-0" data-id="element-2026" />
                    <span className="text-xs text-gray-500" data-id="element-2027">
                      Colors your name & icons
                    </span>
                  </div>
                </div>

                <div data-id="element-2028">
                  <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-2029">
                    Logo Size:{' '}
                    <span className="font-bold text-viridia" data-id="element-2030">{logoSize}</span>px
                  </label>
                  <input type="range" min="60" max="250" value={logoSize} onChange={e => setLogoSize(Number(e.target.value))} className="w-full h-2 bg-gray-800 rounded-lg appearance-none cursor-pointer accent-viridia" data-id="element-2031" />
                </div>

                <div className="pt-3 border-t border-gray-800" data-id="element-2032">
                  <label className="block text-sm font-medium text-gray-300 mb-3" data-id="element-2033">
                    Logo Image
                  </label>
                  <div className="space-y-3" data-id="element-2034">
                    <div className="relative" data-id="element-2035">
                      <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" id="logo-upload" data-id="element-2036" />
                      <label htmlFor="logo-upload" className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-viridia bg-viridia/10 border border-viridia/20 rounded-md cursor-pointer hover:bg-viridia/20 transition-colors" data-id="element-2037">
                        <UploadIcon className="w-4 h-4 mr-2" data-id="element-2038" />
                        Upload Image
                      </label>
                    </div>
                    <div className="text-center text-xs text-gray-500 font-medium uppercase tracking-wider" data-id="element-2039">
                      or
                    </div>
                    <div className="relative" data-id="element-2040">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2041">
                        <LinkIcon className="h-4 w-4 text-gray-500" data-id="element-2042" />
                      </div>
                      <input type="text" value={logoUrl} onChange={e => setLogoUrl(e.target.value)} className="w-full pl-10 pr-3 py-2 bg-viridia-black border border-gray-700 rounded-md focus:outline-none focus:border-viridia focus:ring-1 focus:ring-viridia text-sm text-white" placeholder="Paste image URL here..." data-id="element-2043" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Personal Info */}
            <div className="bg-viridia-surface rounded-xl p-6 border border-gray-800" data-id="element-2044">
              <h2 className="text-xl font-bold text-white mb-4 pb-2 border-b border-gray-800" data-id="element-2045">
                2. Personal Info
              </h2>
              <div className="space-y-4" data-id="element-2046">
                {Object.entries(personalInfo).map(([key, value]) => <div key={key} data-id="element-2047">
                    <label className="block text-sm font-medium text-gray-300 mb-1 capitalize" data-id="element-2048">
                      {key}
                    </label>
                    <input type={key === 'email' ? 'email' : 'text'} name={key} value={value} onChange={handleInfoChange} className="w-full px-3 py-2 bg-viridia-black border border-gray-700 rounded-md focus:outline-none focus:border-viridia focus:ring-1 focus:ring-viridia text-sm text-white" data-id="element-2049" />
                  </div>)}
              </div>
            </div>

            {/* 3. Social Links */}
            <div className="bg-viridia-surface rounded-xl p-6 border border-gray-800" data-id="element-2050">
              <h2 className="text-xl font-bold text-white mb-4 pb-2 border-b border-gray-800" data-id="element-2051">
                3. Social Links
              </h2>
              <div className="bg-viridia-black/50 p-3 rounded border border-gray-800 mb-4 flex items-start gap-2" data-id="element-2052">
                <Wand2Icon className="w-4 h-4 text-viridia flex-shrink-0 mt-0.5" data-id="element-2053" />
                <p className="text-xs text-gray-400" data-id="element-2054">
                  <strong className="text-gray-300" data-id="element-2055">Smart Hide:</strong> Leave a
                  link blank to instantly remove its icon from the signature.
                </p>
              </div>
              <div className="space-y-4" data-id="element-2056">
                {SOCIAL_NETWORKS.map(net => <div key={net.id} data-id="element-2057">
                    <label className="block text-sm font-medium text-gray-300 mb-1" data-id="element-2058">
                      {net.name} URL
                    </label>
                    <input type="text" value={socialLinks[net.id]} onChange={e => handleSocialChange(net.id, e.target.value)} className="w-full px-3 py-2 bg-viridia-black border border-gray-700 rounded-md focus:outline-none focus:border-viridia focus:ring-1 focus:ring-viridia text-sm text-white" placeholder={`https://${net.id}.com/...`} data-id="element-2059" />
                  </div>)}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: PREVIEW & INSTRUCTIONS */}
          <div className="lg:col-span-7 xl:col-span-8 space-y-6" data-id="element-2060">
            {/* Live Preview */}
            <div className="bg-viridia-surface rounded-xl p-6 md:p-8 border border-gray-800 sticky top-24" data-id="element-2061">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 pb-4 border-b border-gray-800 gap-4" data-id="element-2062">
                <h2 className="text-xl font-bold text-white" data-id="element-2063">Live Preview</h2>
                <button onClick={handleCopyClick} className="bg-viridia hover:bg-viridia-dark text-viridia-black font-bold py-2.5 px-6 rounded-lg shadow-lg transition-colors flex items-center justify-center gap-2 text-sm" data-id="element-2064">
                  {isUnlocked ? <>
                      <CopyIcon className="w-4 h-4" data-id="element-2065" /> Copy Signature
                    </> : <>
                      <LockIcon className="w-4 h-4" data-id="element-2066" /> Get Your Signature
                    </>}
                </button>
              </div>

              {/* The actual signature container (white background for email context) */}
              <div className="bg-white p-6 md:p-8 rounded-lg border border-gray-200 overflow-x-auto min-h-[250px]" data-id="element-2067">
                {/* ======================================================= */}
                {/* START OF EMAIL SIGNATURE HTML (Strict Table Layout)     */}
                {/* ======================================================= */}
                <div ref={signatureRef} data-id="element-2068">
                  <table cellPadding="0" cellSpacing="0" border={0} style={{
                  background: 'none',
                  border: 'solid 0px transparent',
                  margin: 0,
                  padding: 0,
                  fontFamily: 'Arial, Helvetica, sans-serif'
                }} data-id="element-2069">
                    <tbody data-id="element-2070">
                      <tr data-id="element-2071">
                        {/* LOGO COLUMN */}
                        <td valign="middle" style={{
                        paddingRight: '20px',
                        textAlign: 'center',
                        verticalAlign: 'middle'
                      }} data-id="element-2072">
                          <a href={formattedWebsite} target="_blank" rel="noopener noreferrer" style={{
                          textDecoration: 'none'
                        }} data-id="element-2073">
                            <img src={logoUrl || 'https://images.unsplash.com/photo-1614680376573-3e4e1ef4142a?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80'} width={logoSize} alt="Company Logo" style={{
                            border: 'none',
                            maxWidth: `${logoSize}px`,
                            display: 'block',
                            borderRadius: '4px'
                          }} data-id="element-2074" />
                          </a>
                        </td>

                        {/* DIVIDER LINE */}
                        <td width="2" style={{
                        backgroundColor: '#8c8c8c',
                        width: '2px',
                        padding: 0
                      }} data-id="element-2075"></td>

                        {/* INFO COLUMN */}
                        <td valign="middle" style={{
                        paddingLeft: '20px',
                        verticalAlign: 'middle'
                      }} data-id="element-2076">
                          {/* Header Text */}
                          <div style={{
                          marginBottom: '8px'
                        }} data-id="element-2077">
                            <span style={{
                            fontFamily: 'Arial, Helvetica, sans-serif',
                            fontSize: '17px',
                            fontWeight: 'bold',
                            color: themeColor,
                            letterSpacing: '2px',
                            textTransform: 'uppercase'
                          }} data-id="element-2078">
                              {personalInfo.name || ' '}
                            </span>
                            <br data-id="element-2079" />
                            <span style={{
                            fontFamily: 'Arial, Helvetica, sans-serif',
                            fontSize: '13px',
                            fontStyle: 'italic',
                            color: '#666666',
                            letterSpacing: '0.5px'
                          }} data-id="element-2080">
                              {personalInfo.role || ' '}
                            </span>
                          </div>

                          {/* Contact Links Table */}
                          <table cellPadding="0" cellSpacing="0" border={0} style={{
                          fontFamily: 'Arial, Helvetica, sans-serif',
                          fontSize: '12px',
                          color: '#444444',
                          lineHeight: '20px'
                        }} data-id="element-2081">
                            <tbody data-id="element-2082">
                              {/* Phone */}
                              {personalInfo.phone && <tr data-id="element-2083">
                                  <td valign="middle" style={{
                                paddingRight: '8px',
                                paddingBottom: '3px'
                              }} data-id="element-2084">
                                    <img src={`https://img.icons8.com/material-rounded/24/${hexNoHash}/phone--v1.png`} alt="Phone" width="16" style={{
                                  display: 'block',
                                  border: 'none'
                                }} data-id="element-2085" />
                                  </td>
                                  <td valign="middle" style={{
                                paddingBottom: '3px'
                              }} data-id="element-2086">
                                    <a href={`tel:${formattedPhone}`} style={{
                                  color: '#444444',
                                  textDecoration: 'none',
                                  fontWeight: 600
                                }} data-id="element-2087">
                                      {personalInfo.phone}
                                    </a>
                                  </td>
                                </tr>}

                              {/* Email */}
                              {personalInfo.email && <tr data-id="element-2088">
                                  <td valign="middle" style={{
                                paddingRight: '8px',
                                paddingBottom: '3px'
                              }} data-id="element-2089">
                                    <img src={`https://img.icons8.com/material-rounded/24/${hexNoHash}/mail.png`} alt="Email" width="16" style={{
                                  display: 'block',
                                  border: 'none'
                                }} data-id="element-2090" />
                                  </td>
                                  <td valign="middle" style={{
                                paddingBottom: '3px'
                              }} data-id="element-2091">
                                    <a href={`mailto:${personalInfo.email}`} style={{
                                  color: '#444444',
                                  textDecoration: 'none',
                                  fontWeight: 600
                                }} data-id="element-2092">
                                      {personalInfo.email}
                                    </a>
                                  </td>
                                </tr>}

                              {/* Location */}
                              {personalInfo.location && <tr data-id="element-2093">
                                  <td valign="middle" style={{
                                paddingRight: '8px',
                                paddingBottom: '3px'
                              }} data-id="element-2094">
                                    <img src={`https://img.icons8.com/material-rounded/24/${hexNoHash}/marker.png`} alt="Location" width="16" style={{
                                  display: 'block',
                                  border: 'none'
                                }} data-id="element-2095" />
                                  </td>
                                  <td valign="middle" style={{
                                paddingBottom: '3px',
                                fontWeight: 600
                              }} data-id="element-2096">
                                    <a href={`https://maps.google.com/?q=${encodedLocation}`} target="_blank" rel="noopener noreferrer" style={{
                                  color: '#444444',
                                  textDecoration: 'none'
                                }} data-id="element-2097">
                                      {personalInfo.location}
                                    </a>
                                  </td>
                                </tr>}

                              {/* Website */}
                              {personalInfo.website && <tr data-id="element-2098">
                                  <td valign="middle" style={{
                                paddingRight: '8px',
                                paddingBottom: '8px'
                              }} data-id="element-2099">
                                    <img src={`https://img.icons8.com/material-rounded/24/${hexNoHash}/domain.png`} alt="Website" width="16" style={{
                                  display: 'block',
                                  border: 'none'
                                }} data-id="element-2100" />
                                  </td>
                                  <td valign="middle" style={{
                                paddingBottom: '8px'
                              }} data-id="element-2101">
                                    <a href={formattedWebsite} target="_blank" rel="noopener noreferrer" style={{
                                  color: '#0000ee',
                                  textDecoration: 'underline',
                                  fontWeight: 600
                                }} data-id="element-2102">
                                      {personalInfo.website}
                                    </a>
                                  </td>
                                </tr>}
                            </tbody>
                          </table>

                          {/* Social Media Icons Table */}
                          <table cellPadding="0" cellSpacing="0" border={0} style={{
                          marginTop: '4px'
                        }} data-id="element-2103">
                            <tbody data-id="element-2104">
                              <tr data-id="element-2105">
                                {SOCIAL_NETWORKS.map(net => {
                                const url = socialLinks[net.id];
                                if (!url || url.trim() === '') return null;
                                return <td key={net.id} style={{
                                  paddingRight: '12px'
                                }} data-id="element-2106">
                                      <a href={url} target="_blank" rel="noopener noreferrer" data-id="element-2107">
                                        <img src={net.icon} alt={net.name} width="22" style={{
                                      display: 'block',
                                      border: 'none'
                                    }} data-id="element-2108" />
                                      </a>
                                    </td>;
                              })}
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                {/* ======================================================= */}
                {/* END OF EMAIL SIGNATURE HTML                             */}
                {/* ======================================================= */}
              </div>

              {/* Instructions */}
              <div className="mt-8 bg-viridia-black/50 border-l-4 border-viridia p-5 rounded-r-lg" data-id="element-2109">
                <h3 className="font-bold text-white mb-3" data-id="element-2110">
                  How to install in Gmail:
                </h3>
                <ol className="list-decimal ml-5 space-y-2 text-gray-400 text-sm" data-id="element-2111">
                  <li data-id="element-2112">
                    Ensure all your links are correct in the inputs on the left.
                  </li>
                  <li data-id="element-2113">
                    Click the{' '}
                    <strong className="text-gray-300" data-id="element-2114">"Copy Signature"</strong>{' '}
                    button above.
                  </li>
                  <li data-id="element-2115">
                    Open Gmail, click the{' '}
                    <strong className="text-gray-300" data-id="element-2116">
                      Settings (Gear) icon
                    </strong>
                    , and click{' '}
                    <strong className="text-gray-300" data-id="element-2117">
                      "See all settings"
                    </strong>
                    .
                  </li>
                  <li data-id="element-2118">
                    Scroll down to the{' '}
                    <strong className="text-gray-300" data-id="element-2119">Signature</strong>{' '}
                    section. Create a new one or edit an existing one.
                  </li>
                  <li data-id="element-2120">
                    Click into the text box and press{' '}
                    <strong className="text-gray-300" data-id="element-2121">Ctrl+V</strong> (Windows)
                    or <strong className="text-gray-300" data-id="element-2122">Cmd+V</strong> (Mac) to
                    paste.
                  </li>
                  <li data-id="element-2123">
                    Scroll to the very bottom of the Gmail settings page and
                    click{' '}
                    <strong className="text-gray-300" data-id="element-2124">Save Changes</strong>.
                  </li>
                </ol>
              </div>

              <div className="mt-6 text-center text-xs text-gray-600" data-id="element-2125">
                Social media and contact icons provided by{' '}
                <a href="https://icons8.com" target="_blank" rel="noopener noreferrer" className="text-viridia hover:underline" data-id="element-2126">
                  Icons8
                </a>
                .
              </div>
            </div>

            {/* Opt-In Modal */}
            {showOptIn && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-2127">
                <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowOptIn(false)} data-id="element-2128"></div>
                <div className="relative bg-viridia-surface border border-gray-800 rounded-2xl p-8 max-w-md w-full shadow-2xl" data-id="element-2129">
                  <button onClick={() => setShowOptIn(false)} className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors" aria-label="Close" data-id="element-2130">
                    <XIcon className="w-5 h-5" data-id="element-2131" />
                  </button>

                  <div className="text-center mb-6" data-id="element-2132">
                    <div className="w-14 h-14 rounded-full bg-viridia/20 flex items-center justify-center mx-auto mb-4" data-id="element-2133">
                      <Wand2Icon className="w-7 h-7 text-viridia" data-id="element-2134" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2" data-id="element-2135">
                      Your Signature is Ready!
                    </h3>
                    <p className="text-gray-400 text-sm" data-id="element-2136">
                      Enter your info below to copy your signature. We'll also
                      send you a backup copy via email.
                    </p>
                  </div>

                  <form onSubmit={handleOptInSubmit} className="space-y-4" data-id="element-2137">
                    <div data-id="element-2138">
                      <label htmlFor="optin-name" className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-2139">
                        Full Name *
                      </label>
                      <input type="text" id="optin-name" required value={optInData.name} onChange={e => setOptInData(prev => ({
                    ...prev,
                    name: e.target.value
                  }))} placeholder="John Smith" className="w-full px-4 py-3 bg-viridia-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-viridia focus:ring-1 focus:ring-viridia transition-colors" data-id="element-2140" />
                    </div>
                    <div data-id="element-2141">
                      <label htmlFor="optin-email" className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-2142">
                        Email Address *
                      </label>
                      <input type="email" id="optin-email" required value={optInData.email} onChange={e => setOptInData(prev => ({
                    ...prev,
                    email: e.target.value
                  }))} placeholder="john@example.com" className="w-full px-4 py-3 bg-viridia-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-viridia focus:ring-1 focus:ring-viridia transition-colors" data-id="element-2143" />
                    </div>
                    <div data-id="element-2144">
                      <label htmlFor="optin-business" className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-2145">
                        Business Name
                      </label>
                      <input type="text" id="optin-business" value={optInData.businessName} onChange={e => setOptInData(prev => ({
                    ...prev,
                    businessName: e.target.value
                  }))} placeholder="Your Company LLC" className="w-full px-4 py-3 bg-viridia-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-viridia focus:ring-1 focus:ring-viridia transition-colors" data-id="element-2146" />
                    </div>

                    <div className="flex items-start gap-3" data-id="element-2147">
                      <input type="checkbox" id="optin-consent" checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)} className="mt-1 w-4 h-4 rounded border-gray-600 bg-viridia-black text-viridia focus:ring-viridia focus:ring-offset-0 cursor-pointer flex-shrink-0" data-id="element-2148" />
                      <label htmlFor="optin-consent" className="text-xs text-gray-400 cursor-pointer leading-relaxed" data-id="element-2149">
                        I agree to receive marketing and promotional emails from
                        Viridia Analytics LLC, including product updates, tips,
                        and special offers. You can unsubscribe at any time.
                        View our{' '}
                        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-viridia hover:underline" data-id="element-2150">
                          Privacy Policy
                        </a>
                        .
                      </label>
                    </div>

                    {optInError && <p className="text-red-400 text-sm text-center" data-id="element-2151">
                        {optInError}
                      </p>}

                    <button type="submit" disabled={optInSubmitting || !consentChecked} className="w-full flex items-center justify-center gap-2 px-6 py-3.5 text-base font-bold text-viridia-black bg-viridia rounded-lg hover:bg-viridia-dark transition-all duration-300 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2152">
                      {optInSubmitting ? <>
                          <LoaderIcon className="w-5 h-5 animate-spin" data-id="element-2153" />{' '}
                          Unlocking...
                        </> : <>
                          <CopyIcon className="w-5 h-5" data-id="element-2154" /> Copy My Signature
                        </>}
                    </button>
                  </form>
                </div>
              </div>}
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && <div className="fixed bottom-5 right-5 z-50 flex flex-col gap-2 animate-in slide-in-from-bottom-5 fade-in duration-300" data-id="element-2155">
          <div className={`flex items-center p-4 rounded-lg shadow-lg border-l-4 ${toast.type === 'success' ? 'bg-viridia-surface border-viridia text-white' : 'bg-red-900/50 border-red-500 text-white'}`} data-id="element-2156">
            {toast.type === 'success' ? <CheckCircleIcon className="w-5 h-5 text-viridia mr-3" data-id="element-2157" /> : <AlertCircleIcon className="w-5 h-5 text-red-500 mr-3" data-id="element-2158" />}
            <div className="font-medium text-sm" data-id="element-2159">{toast.message}</div>
          </div>
        </div>}
    </div>;
}